import {
	useDeleteResponsibleGamingLimitMutation,
	useSetResponsibleGamingLimitMutation,
} from "@api/accountManagement";
import { Form } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LIMIT_PERIODS, LIMIT_TYPES, RG_ERROR_CODES } from "../../constants";
import {
	buildPeriodTableRows,
	clampToBackofficeMax,
	findLimit,
	formatLimitValue,
	formatPendingCountdown,
	getInputValueFromLimit,
	getRgErrorMessage,
	parseInputToApiValue,
} from "../../utils";

const useController = ({ type, limits, currency, translate, showAlert }) => {
	const [period, setPeriod] = useState(LIMIT_PERIODS.ROLLING_24H);
	const [form] = Form.useForm();
	const [formError, setFormError] = useState(null);

	const [setLimit, { isLoading: isSetting }] = useSetResponsibleGamingLimitMutation();
	const [deleteLimit, { isLoading: isDeleting }] = useDeleteResponsibleGamingLimitMutation();

	const limit = useMemo(() => findLimit(limits, type, period), [limits, type, period]);

	const tableRows = useMemo(
		() => buildPeriodTableRows(type, limits, translate),
		[limits, translate, type]
	);

	const hasPending = Boolean(limit?.pending);
	const isSessionTime = type === LIMIT_TYPES.SESSION_TIME;
	const isAmountType = type === LIMIT_TYPES.DEPOSIT || type === LIMIT_TYPES.LOSS;

	useEffect(() => {
		form.setFieldsValue({ value: getInputValueFromLimit(type, limit) });
		setFormError(null);
	}, [form, limit, type, period]);

	const handleSetLimit = useCallback(
		async (values) => {
			if (hasPending) {
				setFormError(translate("limits.errors.pendingLimitChange"));
				return;
			}

			const apiValue = parseInputToApiValue(type, values.value);

			if (apiValue == null) {
				setFormError(
					isSessionTime
						? translate("limits.errors.sessionTimeMin")
						: translate("limits.errors.invalidParam")
				);
				return;
			}

			const clampedValue = clampToBackofficeMax(apiValue, limit?.backofficeMax);

			if (limit?.backofficeMax != null && apiValue > limit.backofficeMax) {
				setFormError(translate("limits.errors.exceedsBackofficeLimit"));
				return;
			}

			try {
				await setLimit({ type, period, value: clampedValue }).unwrap();
				showAlert({
					type: "success",
					msg: "limits.setSuccess",
					title: "SUCCESS",
				});
				setFormError(null);
			} catch (error) {
				const code = error?.data?.code;

				if (code === RG_ERROR_CODES.PENDING_LIMIT_CHANGE) {
					setFormError(translate("limits.errors.pendingLimitChange"));
					return;
				}

				const message = getRgErrorMessage(error, translate);
				setFormError(message);
				showAlert({
					type: "error",
					msg: message,
					title: "ERROR",
					noTranslate: true,
				});
			}
		},
		[hasPending, isSessionTime, limit, period, setLimit, showAlert, translate, type]
	);

	const handleRemoveLimit = useCallback(async () => {
		if (hasPending) {
			setFormError(translate("limits.errors.pendingLimitChange"));
			return;
		}

		try {
			await deleteLimit({ type, period }).unwrap();
			showAlert({
				type: "success",
				msg: "limits.removeSuccess",
				title: "SUCCESS",
			});
			form.resetFields();
			setFormError(null);
		} catch (error) {
			const message = getRgErrorMessage(error, translate);
			setFormError(message);
			showAlert({
				type: "error",
				msg: message,
				title: "ERROR",
				noTranslate: true,
			});
		}
	}, [deleteLimit, form, hasPending, period, showAlert, translate, type]);

	const state = useMemo(
		() => ({
			period,
			limit,
			hasPending,
			tableRows,
			isSetting,
			isDeleting,
			formError,
			isSessionTime,
			isAmountType,
			currency,
			pendingText: limit?.pending
				? translate("limits.pending.banner", {
						countdown: formatPendingCountdown(limit.pending.effectiveAt, translate),
						value:
							limit.pending.value == null
								? translate("limits.pending.removal")
								: isSessionTime
									? formatLimitValue(
											{ ...limit, effectiveMax: limit.pending.value },
											translate
										)
									: isAmountType
										? `${limit.pending.value} ${currency || ""}`.trim()
										: String(limit.pending.value),
					})
				: null,
			inputPlaceholder: isSessionTime
				? translate("limits.form.sessionMinutesPlaceholder")
				: isAmountType
					? translate("limits.form.amountPlaceholder")
					: translate("limits.form.countPlaceholder"),
			inputLabel: isSessionTime
				? translate("limits.form.sessionMinutesLabel")
				: isAmountType
					? translate("limits.form.amountLabel")
					: translate("limits.form.countLabel"),
			backofficeHint:
				limit?.backofficeMax != null
					? translate("limits.backofficeCap", {
							value: isSessionTime
								? formatLimitValue(
										{ ...limit, effectiveMax: limit.backofficeMax },
										translate
									)
								: isAmountType
									? `${limit.backofficeMax} ${currency || ""}`.trim()
									: String(limit.backofficeMax),
						})
					: null,
			canRemove: Boolean(limit?.userMax || limit?.effectiveMax),
			isFormDisabled: hasPending || isSetting || isDeleting,
		}),
		[
			currency,
			formError,
			hasPending,
			isAmountType,
			isDeleting,
			isSessionTime,
			isSetting,
			limit,
			period,
			tableRows,
			translate,
		]
	);

	return {
		state,
		handlers: {
			setPeriod,
			handleSetLimit,
			handleRemoveLimit,
		},
		helpers: { form },
	};
};

export default useController;
